import type { Booking } from 'repositories/BerthApi/types/booking'
import type { UpdateBookingParams } from 'repositories/BerthApi/updateBooking/type'
import type { UpdateRepeatBookingParams } from 'repositories/BerthApi/updateRepeatBooking/type'
import type { UpdateSpecificInfoParams } from 'repositories/BerthApi/updateSpecificInfo/type'
import type { UpdateUnassignedBookingParams } from 'repositories/BerthApi/updateUnassignedBooking/type'

import { ADDITIONAL_COLUMN_INDEXES } from './bookingParameters'
import {
  combineEntryInstructionTime,
  normalizeEmail,
  normalizePhone,
  splitEntryInstructionTime,
  toHalfWidth,
} from './editBookingNormalize'
import {
  FIXED_CARGO_COLUMN_KEYS,
  type EditBookingAttachment,
  type EditBookingFormValues,
  type ExportAdditionalColumnKey,
  type FixedCargoColumnKey,
  type ImportAdditionalColumnKey,
} from './editBookingValidation'

type FixedCargoFields = Record<FixedCargoColumnKey, string>
type AdditionalColumnFields = Record<ImportAdditionalColumnKey | ExportAdditionalColumnKey, string>

const emptyFixedCargoFields = (): FixedCargoFields =>
  FIXED_CARGO_COLUMN_KEYS.reduce((acc, key) => {
    acc[key] = ''
    return acc
  }, {} as FixedCargoFields)

const emptyAdditionalColumnFields = (): AdditionalColumnFields => {
  const acc = {} as AdditionalColumnFields
  for (const i of ADDITIONAL_COLUMN_INDEXES) {
    acc[`importAdditionalColumn${i}` as ImportAdditionalColumnKey] = ''
    acc[`exportAdditionalColumn${i}` as ExportAdditionalColumnKey] = ''
  }
  return acc
}

export const EMPTY_FORM_VALUES: EditBookingFormValues = {
  berthId: '',
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
  adminNote: '',
  companyName: '',
  vehicleControllerName: '',
  vehicleControllerPhone: '',
  vehicleControllerEmail: '',
  driverCompanyName: '',
  vehicleNumber: '',
  vehicleCategory: '',
  driverName: '',
  driverPhone: '',
  driverEmail: '',
  companyId: null,
  driverCompanyId: null,
  driverId: null,
  vehicleControllerId: null,
  importFlag: false,
  exportFlag: false,
  driverDateTime: '',
  driverCombinedTime: '',
  skipInstructionTime: false,
  attachments: [],
  deleteAttachmentIds: [],
  ...emptyFixedCargoFields(),
  ...emptyAdditionalColumnFields(),
}

// YYYYMMDD → YYYY-MM-DD
const toFormDate = (d: string | null): string => {
  if (!d || d.length !== 8) return ''
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`
}

// HH:mm:ss → HH:mm
const toFormTime = (t: string | null): string => (t ? t.slice(0, 5) : '')

// YYYY-MM-DD → YYYYMMDD
const toApiDate = (d: string): string => d.replace(/-/g, '')
// HH:mm → HH:mm:ss
const toApiTime = (t: string): string => `${t}:00`

// 空文字 → null（API は null を期待）
export const emptyToNull = (v: string): string | null => (v === '' ? null : v)

const toFormNumberString = (v: number | string | null): string => {
  if (v === null || v === undefined || v === '') return ''
  return String(v)
}

const toApiNumber = (v: string): number | null => {
  if (v === '') return null
  const n = Number(toHalfWidth(v))
  return Number.isFinite(n) ? n : null
}

const collectCargoFromBooking = (booking: Booking): FixedCargoFields & AdditionalColumnFields => {
  const fixed = {} as FixedCargoFields
  for (const key of FIXED_CARGO_COLUMN_KEYS) {
    if (key === 'importGoodsAmount' || key === 'exportGoodsAmount') {
      fixed[key] = toFormNumberString(booking[key])
      continue
    }
    fixed[key] = (booking[key] as string | null) ?? ''
  }
  const additional = {} as AdditionalColumnFields
  for (const i of ADDITIONAL_COLUMN_INDEXES) {
    const ik = `importAdditionalColumn${i}` as ImportAdditionalColumnKey
    const ek = `exportAdditionalColumn${i}` as ExportAdditionalColumnKey
    additional[ik] = (booking[ik] as string | null) ?? ''
    additional[ek] = (booking[ek] as string | null) ?? ''
  }
  return { ...fixed, ...additional }
}

export const toFormValues = (booking: Booking): EditBookingFormValues => {
  const startDate = toFormDate(booking.startDate || booking.planStartDate)
  const startTime = toFormTime(booking.startTime || booking.planStartTime)
  const endDate = toFormDate(booking.endDate || booking.planEndDate)
  const endTime = toFormTime(booking.endTime || booking.planEndTime)

  const { date: driverDateTime, time: driverCombinedTime } = splitEntryInstructionTime(
    booking.entryInstructionTime
  )

  return {
    berthId: booking.berthId ?? '',
    startDate,
    startTime,
    endDate,
    endTime,
    adminNote: booking.adminNote ?? '',
    companyName: booking.companyName ?? '',
    vehicleControllerName: booking.vehicleControllerName ?? '',
    vehicleControllerPhone: booking.vehicleControllerPhone ?? '',
    vehicleControllerEmail: booking.vehicleControllerEmail ?? '',
    driverCompanyName: booking.driverCompanyName ?? '',
    vehicleNumber: booking.vehicleNumber ?? '',
    vehicleCategory: booking.vehicleCategory ?? '',
    driverName: booking.driverName ?? '',
    driverPhone: booking.driverPhone ?? '',
    driverEmail: booking.driverEmail ?? '',
    companyId: booking.companyId ?? null,
    driverCompanyId: booking.driverCompanyId ?? null,
    driverId: booking.driverId ?? null,
    vehicleControllerId: booking.vehicleControllerId ?? null,
    importFlag: (booking.importFlag ?? 0) === 1,
    exportFlag: (booking.exportFlag ?? 0) === 1,
    driverDateTime,
    driverCombinedTime,
    skipInstructionTime: booking.skipInstructionTime ?? false,
    attachments: (booking.attachments ?? [])
      .filter((a) => a.deletedAt == null)
      .map<EditBookingAttachment>(({ id, name, size }) => ({ id, name, size })),
    deleteAttachmentIds: [],
    ...collectCargoFromBooking(booking),
  }
}

type AdditionalApiPayload = Record<
  ImportAdditionalColumnKey | ExportAdditionalColumnKey,
  string | null
>

const buildSharedFields = (values: EditBookingFormValues, booking: Booking) => {
  const additional = ADDITIONAL_COLUMN_INDEXES.reduce<AdditionalApiPayload>((acc, i) => {
    const ik = `importAdditionalColumn${i}` as const
    const ek = `exportAdditionalColumn${i}` as const
    acc[ik] = emptyToNull(values[ik])
    acc[ek] = emptyToNull(values[ek])
    return acc
  }, {} as AdditionalApiPayload)

  return {
    planStartDate: toApiDate(values.startDate),
    planStartTime: toApiTime(values.startTime),
    planEndDate: toApiDate(values.endDate),
    planEndTime: toApiTime(values.endTime),
    driverCompanyName: emptyToNull(values.driverCompanyName),
    driverName: emptyToNull(values.driverName),
    driverPhone: emptyToNull(normalizePhone(values.driverPhone)),
    driverEmail: emptyToNull(normalizeEmail(values.driverEmail)),
    companyName: emptyToNull(values.companyName),
    vehicleControllerName: emptyToNull(values.vehicleControllerName),
    vehicleControllerPhone: emptyToNull(normalizePhone(values.vehicleControllerPhone)),
    vehicleControllerEmail: emptyToNull(normalizeEmail(values.vehicleControllerEmail)),
    importFlag: (values.importFlag ? 1 : 0) as 0 | 1,
    exportFlag: (values.exportFlag ? 1 : 0) as 0 | 1,
    needConfirmFlag: (booking.needConfirmFlag ?? 0) as 0 | 1,
    // ComboBox / 入力で編集可能なフィールドはフォーム値を使う。
    companyId: values.companyId,
    driverCompanyId: values.driverCompanyId,
    driverId: values.driverId,
    vehicleNumber: emptyToNull(toHalfWidth(values.vehicleNumber)),
    vehicleCategory: emptyToNull(values.vehicleCategory),
    vehicleControllerId: values.vehicleControllerId,
    // 荷物情報（1-C-3 でフォーム経由）
    importDeliverySlipNumber: emptyToNull(values.importDeliverySlipNumber),
    exportDeliverySlipNumber: emptyToNull(values.exportDeliverySlipNumber),
    importMainGoods: emptyToNull(values.importMainGoods),
    exportMainGoods: emptyToNull(values.exportMainGoods),
    importPackageType: emptyToNull(values.importPackageType),
    exportPackageType: emptyToNull(values.exportPackageType),
    importGoodsAmount: toApiNumber(values.importGoodsAmount),
    exportGoodsAmount: toApiNumber(values.exportGoodsAmount),
    importContactNotes: emptyToNull(values.importContactNotes),
    exportContactNotes: emptyToNull(values.exportContactNotes),
    importGoodsOwner: emptyToNull(values.importGoodsOwner),
    exportGoodsOwner: emptyToNull(values.exportGoodsOwner),
    importGoodsName: emptyToNull(values.importGoodsName),
    exportGoodsName: emptyToNull(values.exportGoodsName),
    destination: emptyToNull(values.destination),
    ...additional,
    // 編集 UI で扱わない既存値は booking から直接転送。
    // BE 側 convertToBookingEntity は ObjectMapperUtils.map(reqDto, entity) で
    // 送信されなかったフィールドを null で上書きするため、PC 版に揃えて素通しで保護する。
    currentDriverCompanyId: booking.currentDriverCompanyId ?? null,
    currentDriverCompanyName: booking.currentDriverCompanyName ?? null,
    // apply_type は予約の値ではなく操作種別を示す定数（PC版 SlideModal/Reservations/index.js:682）。
    // 2 = UPDATE_BOOKING（編集）固定。booking.applyType を転送すると 1 (CREATE) 等の異常値が送られる。
    applyType: 2 as const,
    assignedPlanStartDatetime: booking.assignedPlanStartDatetime ?? null,
    assignedPlanEndDatetime: booking.assignedPlanEndDatetime ?? null,
    delegationId: booking.delegationId ?? null,
  }
}

export const toUpdateBookingParams = (
  values: EditBookingFormValues,
  booking: Booking,
  tenantId: string,
  warehouseId: string
): UpdateBookingParams => ({
  bookingId: booking.bookingId ?? '',
  tenantId,
  warehouseId,
  berthId: values.berthId,
  ...buildSharedFields(values, booking),
  ...buildAttachmentFields(values),
})

export const toUpdateUnassignedBookingParams = (
  values: EditBookingFormValues,
  booking: Booking,
  tenantId: string,
  warehouseId: string
): UpdateUnassignedBookingParams => ({
  bookingId: booking.bookingId ?? '',
  tenantId,
  warehouseId,
  ...buildSharedFields(values, booking),
  ...buildAttachmentFields(values),
})

// 編集前の元の開始/終了日時を YYYY-MM-DD HH:mm:ss 形式に整形。
// 繰り返し API はこの値で「どのインスタンスを更新するか」を識別する（編集後の値ではないことに注意）。
// PC 版 (SlideModal/Reservations) と同じく booking.start_date+start_time / end_date+end_time
// をそのまま使う。plan_* フォールバックは持たない（BE の楽観ロックは start_*/end_* との一致を見るため）。
const toOriginalDatetime = (date: string | null, time: string | null): string => {
  if (!date || !time) return ''
  return `${toFormDate(date)} ${time}`
}

export const toUpdateRepeatBookingParams = (
  values: EditBookingFormValues,
  booking: Booking,
  tenantId: string,
  warehouseId: string
): UpdateRepeatBookingParams => ({
  bookingId: booking.bookingId ?? '',
  repeatId: booking.repeatId ?? '',
  tenantId,
  warehouseId,
  berthId: values.berthId,
  ...buildSharedFields(values, booking),
  ...buildAttachmentFields(values),
  // PC 版 (validations.js:176-179) に揃え、start_*/end_* も form の plan_* と同値で送る
  startDate: toApiDate(values.startDate),
  startTime: toApiTime(values.startTime),
  endDate: toApiDate(values.endDate),
  endTime: toApiTime(values.endTime),
  startDatetime: toOriginalDatetime(booking.startDate, booking.startTime),
  endDatetime: toOriginalDatetime(booking.endDate, booking.endTime),
})

/**
 * 添付ファイルの送信フィールドを組み立てる。
 * - attachments: 新規追加分（id 未付与）のみ。base64 dataURL を content に持つ
 * - deleteAttachmentIds: 削除対象の既存添付 ID
 *
 * 添付資料は予約情報セクションの保存ボタンに統合され、通常予約更新・繰り返し予約更新の
 * いずれの payload にも乗る。
 */
const buildAttachmentFields = (
  values: EditBookingFormValues
): Pick<UpdateBookingParams, 'attachments' | 'deleteAttachmentIds'> => ({
  attachments: values.attachments
    .filter((a) => a.id == null)
    .map(({ name, size, lastModified, content }) => ({ name, size, lastModified, content })),
  deleteAttachmentIds: values.deleteAttachmentIds,
})

export const toUpdateSpecificInfoParams = (
  values: EditBookingFormValues,
  booking: Booking
): UpdateSpecificInfoParams => ({
  bookingId: booking.bookingId ?? '',
  adminNote: emptyToNull(values.adminNote.trim()),
  entryInstructionTime: values.skipInstructionTime
    ? null
    : combineEntryInstructionTime(values.driverDateTime, values.driverCombinedTime),
  skipInstructionTime: values.skipInstructionTime,
  // PC 版互換で常に true 固定。BE 側のコメント自動生成・updated_by 更新の副作用に必要
  isUpdateBooking: true,
})
